import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { useLeadForm } from '../hooks/useLeadForm';
import { getDialCodeFromCountry, getCountryCodesForSelect } from '../utils/countryMapping';

export const PopupForm = ({ isOpen, onClose }) => {
  const { geolocationData, isLoading, error, submitForm, clearError } = useLeadForm();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    contactNumber: '',
    message: ''
  });

  const countryCodesOptions = getCountryCodesForSelect();

  useEffect(() => {
    if (geolocationData?.data?.countryCode) {
      const countryCode = geolocationData.data.countryCode;
      const dialCode = getDialCodeFromCountry(countryCode);
      setFormData(prev => ({
        ...prev,
        country: countryCode,
        countryCode: `+${dialCode}`
      }));
    }
  }, [geolocationData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    await submitForm(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="popup-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#58225a]">Protect Your Brand — Start Here</DialogTitle>
          <p className="text-sm text-gray-500 mt-1">Fill out the form and our attorneys will review your case at no cost</p>
        </DialogHeader>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="popup-form-error">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="popup-form">
          <div>
            <Label htmlFor="popup-name">Name *</Label>
            <Input id="popup-name" data-testid="popup-form-name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" placeholder="Your full name" disabled={isLoading} />
          </div>

          <div>
            <Label htmlFor="popup-email">Email *</Label>
            <Input id="popup-email" data-testid="popup-form-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1" placeholder="Your email address" disabled={isLoading} />
          </div>

          <div>
            <Label htmlFor="popup-contact">Contact Number *</Label>
            <div className="flex gap-2 mt-1">
              <Select value={formData.countryCode} onValueChange={(value) => { const sc = countryCodesOptions.find(c => c.code === value); setFormData({ ...formData, countryCode: value, country: sc?.countryCode || 'US' }); }} disabled={isLoading}>
                <SelectTrigger className="w-32" data-testid="popup-form-country-code"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {countryCodesOptions.map((item) => (<SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input id="popup-contact" data-testid="popup-form-phone" type="tel" required value={formData.contactNumber} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v.length <= 15) setFormData({ ...formData, contactNumber: v }); }} placeholder="Phone number" className="flex-1" maxLength={15} disabled={isLoading} />
            </div>
          </div>

          <div>
            <Label htmlFor="popup-message">Message</Label>
            <Textarea id="popup-message" data-testid="popup-form-message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="mt-1" placeholder="Describe your trademark needs..." rows={4} disabled={isLoading} />
          </div>

          <Button type="submit" data-testid="popup-form-submit" className="w-full bg-[#ba2a84] hover:bg-[#8a1f63]" disabled={isLoading}>
            {isLoading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
